import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'

import SearchPage from 'ui/pages/search'
import ItemSearch from 'ui/modules/lists/searchList/item'
import Breadcrumb from 'ui/components/breadcrumb'

import { fetchSearches, fakeRemoveSearches } from 'services/searches/actions'
import { getSearches, getIsSearchesFetching } from 'services/searches/selectors'
import { getSearchConfig } from 'services/config/selectors'
import * as actionTypesSearches from 'services/searches/action-types'
import { showSnackbar } from 'services/snackbar/actions'

const Searches = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [selected, setSelected] = useState({})

  const searches = useSelector(getSearches)
  const isLoading = useSelector(getIsSearchesFetching)
  const searchConfig = useSelector(getSearchConfig)

  useEffect(() => {
    dispatch(fetchSearches())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelect = useCallback(id => {
    setSelected(selected => {
      if (selected[id]) {
        delete selected[id]
      } else {
        selected[id] = true
      }
      return { ...selected }
    })
  }, [])

  const totalSelected = useMemo(() => Object.values(selected).length, [
    selected,
  ])

  const handleBulkSelect = useCallback(() => {
    if (totalSelected === 0) {
      const ids = searches.map(search => search.id)
      const newSelected = {}
      ids.forEach(vendorId => (newSelected[vendorId] = true))
      setSelected(newSelected)
    } else {
      setSelected({})
    }
  }, [searches, totalSelected])

  const handleRemove = useCallback(() => {
    const ids = Object.keys(selected)
    const message =
      ids.length > 1 ? 'searches:removed-many' : 'searches:removed-one'
    dispatch(fakeRemoveSearches(ids))
    setSelected({})
    dispatch(
      showSnackbar({
        message,
        actionToDo: {
          type: actionTypesSearches.REMOVE,
          payload: { ids },
        },
        actionOnCancel: {
          type: actionTypesSearches.FETCH_SEARCHES,
        },
      }),
    )
  }, [dispatch, selected])

  const handleSearch = useCallback(
    params => {
      history.push({
        pathname: '/app/searches/new',
        search: `?${params}`,
      })
    },
    [history],
  )

  return !isLoading ? (
    <SearchPage
      isLoading={isLoading}
      searchConfig={searchConfig}
      totalElements={searches.length}
      selectedCount={totalSelected}
      handleBulkSelect={handleBulkSelect}
      onRemoveVendors={handleRemove}
      onSearch={handleSearch}
    >
      {searches.map(search => (
        <ItemSearch
          key={search.id}
          id={search.id}
          title={search.name}
          subtitle={
            <Breadcrumb
              links={search.cpvInfo ? search.cpvInfo.slice(0, -1) : ''}
              current={
                search.cpvInfo && search.cpvInfo.slice(-1)[0]
                  ? search.cpvInfo.slice(-1)[0].label
                  : ''
              }
            />
          }
          linkTo={`/app/searches/${search.id}`}
          onSelect={handleSelect}
          isSelected={!!selected[search.id]}
          totalResults={search.total}
        />
      ))}
    </SearchPage>
  ) : (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress color="secondary" />
    </Box>
  )
}

export default Searches
